import React from 'react';
import { FormField, JalaliDatePicker, SegmentedControl, Select, Textarea } from '@parsina/framework';

import { useTranslation } from '../../i18n';
import { OVERRIDE_CAPABILITIES, capabilityLabelKey } from '../../constants/capabilities';
import TaskDialog from '../../components/TaskDialog';

const REASON_MAX = 300;
const REASON_MIN = 3;
const tomorrow = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
};

/**
 * Open (or close) one capability, independently of the plan and of the
 * subscription's state.
 *
 * This is the un-break path, and it matters more since tiering: one override
 * clears the plan gate and the expiry gate together. The BFF has exposed
 * `grantOverride` since day one and no screen ever called it, so support's only
 * route was a gRPC call by hand.
 *
 * ── THE CAPABILITY IS PICKED, NEVER TYPED ───────────────────────────────────
 * A capability is a GraphQL field name checked by name downstream, so free text
 * is a typo that grants nothing and reports success — the ticket gets closed and
 * the clinic still cannot book.
 *
 * ── REVOKE AND EXPIRY EXIST IN THE API ──────────────────────────────────────
 * `granted: false` and `expiresAt` were both available and neither was reachable,
 * so an override was permanent and one-way: the only way to take one back was
 * another hand-written gRPC call. A time-boxed grant is also the right default
 * shape for "unblock them until the invoice clears".
 */
export default function OverrideDialog({ open, onOpenChange, pending, error, onSubmit }) {
  const { t } = useTranslation();

  const [capability, setCapability] = React.useState('');
  const [granted, setGranted] = React.useState('grant');
  const [expiresAt, setExpiresAt] = React.useState('');
  const [reason, setReason] = React.useState('');

  React.useEffect(() => {
    if (!open) return;
    setCapability('');
    setGranted('grant');
    setExpiresAt('');
    setReason('');
  }, [open]);

  const trimmed = reason.trim();
  const expiryError = expiresAt && expiresAt < tomorrow() ? t('validation.futureRequired') : null;
  const dirty = !!(capability || expiresAt || reason);

  return (
    <TaskDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('clinic.dialogs.override.title')}
      error={error}
      pending={pending}
      dirty={dirty}
      submitLabel={granted === 'grant'
        ? t('clinic.dialogs.override.submitGrant')
        : t('clinic.dialogs.override.submitRevoke')}
      submitVariant={granted === 'grant' ? 'default' : 'destructive'}
      submitDisabled={!capability || trimmed.length < REASON_MIN || !!expiryError}
      onSubmit={() => onSubmit({
        capability,
        granted: granted === 'grant',
        reason: trimmed,
        expiresAt: expiresAt || null,
      })}
      data-testid="detail-override-confirm"
    >
      <p className="text-sm leading-6 text-muted-foreground">
        {t('clinic.dialogs.override.body')}
      </p>

      <div data-testid="detail-override-capability">
        <Select
          label={t('clinic.dialogs.override.capability')}
          required
          value={capability}
          onChange={(e) => setCapability(e.target?.value ?? e)}
          options={OVERRIDE_CAPABILITIES.map((value) => ({
            value,
            label: t(capabilityLabelKey(value)),
          }))}
          placeholder={t('clinic.dialogs.override.capabilityPlaceholder')}
        />
      </div>

      <FormField label={t('clinic.dialogs.override.mode')}>
        <div data-testid="detail-override-granted">
          <SegmentedControl
            semantics="filter"
            size="sm"
            ariaLabel={t('clinic.dialogs.override.mode')}
            options={[
              { value: 'grant', label: t('clinic.dialogs.override.grant') },
              { value: 'revoke', label: t('clinic.dialogs.override.revoke') },
            ]}
            value={granted}
            onChange={setGranted}
          />
        </div>
      </FormField>

      <FormField
        label={t('clinic.dialogs.override.expires')}
        error={expiryError || undefined}
        helperText={expiryError ? undefined : t('clinic.dialogs.override.expiresHelper')}
      >
        <div data-testid="detail-override-expires">
          <JalaliDatePicker value={expiresAt} onChange={setExpiresAt} />
        </div>
      </FormField>

      <div data-testid="detail-override-reason">
        <Textarea
          label={t('clinic.dialogs.override.reason')}
          required
          value={reason}
          onChange={(e) => setReason(e.target?.value ?? e)}
          placeholder={t('clinic.dialogs.override.reasonPlaceholder')}
          maxLength={REASON_MAX}
          showCount
          rows={2}
        />
      </div>
    </TaskDialog>
  );
}
